/**
 * v0.16.3 (B4) — armazenamento real no aparelho (IndexedDB). Só roda no navegador; a lógica fica em `gpsSync.ts`.
 * Dois "stores": `sessions` (resumo, fase, corpo pendente) e `events` (chave [id, seq]). As coordenadas ficam só no
 * aparelho e só até o servidor confirmar; sessões são filtradas por usuário.
 */
import type { Ev } from "./gps";
import type { GpsStore, Session } from "./gpsSync";

const DB = "eapace-gps";

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => { req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error); });
}
function done(tx: IDBTransaction): Promise<void> {
  return new Promise((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); tx.onabort = () => rej(tx.error); });
}

/** Abre o banco; devolve null se o navegador não oferece IndexedDB (ex.: alguns modos privados). */
export async function openGpsStore(): Promise<GpsStore | null> {
  if (typeof indexedDB === "undefined") return null;
  let db: IDBDatabase;
  try {
    db = await new Promise<IDBDatabase>((res, rej) => {
      const open = indexedDB.open(DB, 1);
      open.onupgradeneeded = () => {
        const d = open.result;
        d.createObjectStore("sessions", { keyPath: "id" }).createIndex("userId", "userId");
        d.createObjectStore("events", { keyPath: ["id", "seq"] });
      };
      open.onsuccess = () => res(open.result);
      open.onerror = () => rej(open.error);
      open.onblocked = () => rej(new Error("blocked"));
    });
  } catch { return null; }

  const range = (id: string) => IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER]);
  return {
    async putSession(s: Session) { const tx = db.transaction("sessions", "readwrite"); tx.objectStore("sessions").put(s); await done(tx); },
    async getSession(id: string) { return wrap<Session | undefined>(db.transaction("sessions").objectStore("sessions").get(id)); },
    async listSessions(userId: string) { return wrap<Session[]>(db.transaction("sessions").objectStore("sessions").index("userId").getAll(userId)); },
    async appendEvents(id: string, fromSeq: number, evs: Ev[]) {
      const tx = db.transaction("events", "readwrite");
      const st = tx.objectStore("events");
      evs.forEach((ev, i) => st.put({ id, seq: fromSeq + i, ev }));
      await done(tx);
    },
    async getEvents(id: string) {
      const rows = await wrap<{ seq: number; ev: Ev }[]>(db.transaction("events").objectStore("events").getAll(range(id)));
      return rows.sort((a, b) => a.seq - b.seq).map((r) => r.ev);
    },
    async deleteEvents(id: string) { const tx = db.transaction("events", "readwrite"); tx.objectStore("events").delete(range(id)); await done(tx); },
    async deleteSession(id: string) {
      const tx = db.transaction(["sessions", "events"], "readwrite");
      tx.objectStore("sessions").delete(id);
      tx.objectStore("events").delete(range(id));
      await done(tx);
    },
  };
}
